import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Box, Skeleton, Typography } from '@mui/material';
import useAccordion from '../../hooks/useAccordion';
import StyledAccordion, {
  StyledAccordionSummary,
  StyledAccordionActions,
  StyledAccordionDetails,
} from '../CatergoriesAccordion/CatergoriesAccordion.styled';
import PoliciesAccordion from '../PoliciesAccordion/PoliciesAccordion';
import { ICategory, ICategoryInfo } from '../../types/policy-profile-types';
import PoliciesStore from '../../store/policies';
import { useSnapshot } from 'valtio';
import fetchProfilePoliciesByCategoryAction from '../../actions/policies-profile-actions/fetch-profile-policies-by-category-action';
import { IPolicy } from '../../types/policies-types';
import PoliciesProfileUIStateStore from '../../store/policies-profile-ui';
import { debounce, range } from 'lodash';
import { useSearchParams } from 'react-router-dom';
import { useMountEffect } from 'react-utils';
import { CategoryDisplayName } from '@oxappsec/ox-consolidated-categories';
import { generateCategoryIcon } from 'common-icons';

interface AccordionCategoryItemProps {
  category: ICategory;
  categoriesInfo?: ICategoryInfo;
}
const AccordionCategoryItem: React.FC<
  React.PropsWithChildren<AccordionCategoryItemProps>
> = ({ category, categoriesInfo }) => {
  const [expanded, handleChange, setExpanded] = useAccordion();
  const [searchParams] = useSearchParams();
  const catAccordionRef = useRef(null);

  const categoryId = searchParams.get('categoryId');
  const policyId = searchParams.get('policyId');

  const { policies } = useSnapshot(PoliciesStore);
  const { loadingProfilePoliciesByCategory } = useSnapshot(
    PoliciesProfileUIStateStore,
  );

  const [policiesCategory, setPoliciesCategory] = useState<Array<IPolicy>>([]);
  useEffect(() => {
    setPoliciesCategory(
      Object.values(policies)
        .filter(p => p.catId === category.id)
        .sort((p1, p2) => p1.order - p2.order),
    );
  }, [category.id, policies]);

  useMountEffect(() => {
    const catId = categoryId && parseInt(categoryId);
    if (catId) {
      setExpanded(catId);
    }
  });

  const CategoryIcon = useMemo(() => {
    return generateCategoryIcon(category.displayName as CategoryDisplayName);
  }, [category.displayName]);

  useEffect(() => {
    if (expanded === category.id && policiesCategory.length === 0) {
      fetchProfilePoliciesByCategoryAction(category.id);
      if (catAccordionRef && catAccordionRef.current) {
        const elem = catAccordionRef.current as HTMLElement;
        debounce(() => {
          elem.scrollIntoView({
            behavior: 'smooth',
            inline: 'nearest',
          });
        }, 500)();
      }
    }
  }, [category.id, expanded, policiesCategory.length, policyId]);

  return (
    <div data-category-id={category.id} ref={catAccordionRef}>
      <StyledAccordion
        expanded={expanded === category.id}
        onChange={handleChange(category.id, `?categoryId=${category.id}`)}>
        <StyledAccordionSummary>
          <Typography
            gap={1}
            display='flex'
            alignItems='center'
            variant='body2'
            color='text.primary'>
            {CategoryIcon && <CategoryIcon />}
            {category.displayName}
            {categoriesInfo && (
              <span>
                {' '}
                ({categoriesInfo.enbaledPolicies} of{' '}
                {categoriesInfo.totalPolicies} policies enabled)
              </span>
            )}
          </Typography>
        </StyledAccordionSummary>
        <StyledAccordionDetails>
          <Typography pl={2} color='text.secondary' variant='body2'>
            {category.description}
          </Typography>
        </StyledAccordionDetails>
        <StyledAccordionActions>
          {loadingProfilePoliciesByCategory &&
          expanded === Number(categoryId) ? (
            <Box minWidth='100%'>
              {range(1, 10).map((_, index) => (
                <Skeleton key={index} animation='wave' />
              ))}
            </Box>
          ) : (
            <PoliciesAccordion policies={policiesCategory} />
          )}
        </StyledAccordionActions>
      </StyledAccordion>
    </div>
  );
};

export default AccordionCategoryItem;
